import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  CheckCircle,
  Timer as TimerIcon,
  AccountBalance,
} from '@mui/icons-material';
import { apiService, AutoTradingStatus } from '../services/api';

const AutoTrading: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<AutoTradingStatus | null>(null);
  const [processing, setProcessing] = useState(false);
  const [monitoring, setMonitoring] = useState(false);
  const [lastProcessResult, setLastProcessResult] = useState<any>(null);
  const [lastMonitorResult, setLastMonitorResult] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiService.getAutoTradingStatus();
      setStatus(data);
    } catch (err: any) {
      console.error('Failed to fetch auto-trading status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch status');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchStatus().finally(() => setLoading(false));

    // Auto-refresh every 10 seconds
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(fetchStatus, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchStatus, autoRefresh]);

  const handleToggleAutoTrading = async (bucketId: number, currentlyEnabled: boolean) => {
    try {
      if (currentlyEnabled) {
        await apiService.disableAutoTrading(bucketId);
      } else {
        await apiService.enableAutoTrading(bucketId);
      }
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle auto-trading');
    }
  };

  const handleProcessOpportunities = async (bucketId?: number) => {
    setProcessing(true);
    setError(null);
    try {
      const result = await apiService.autoProcessOpportunities(bucketId);
      setLastProcessResult(result);
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to process opportunities');
    } finally {
      setProcessing(false);
    }
  };

  const handleMonitorPositions = async (bucketId?: number) => {
    setMonitoring(true);
    setError(null);
    try {
      const result = await apiService.autoMonitorPositions(bucketId);
      setLastMonitorResult(result);
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to monitor positions');
    } finally {
      setMonitoring(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Auto-Trading Control Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automated position management and opportunity processing
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label="Auto-refresh"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchStatus()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {status && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalance color="primary" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Total Buckets
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {status.summary.total_buckets}
                </Typography>
                <Typography variant="caption" color="success.main">
                  {status.summary.auto_trading_enabled_buckets} auto-enabled
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUp color="success" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Available Opportunities
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {status.summary.total_available_opportunities}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ready to process
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TimerIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Monitored Positions
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {status.summary.total_monitored_positions}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Open positions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    System Status
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  Online
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(status.timestamp).toLocaleTimeString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Action Buttons */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Manual Controls
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manually trigger auto-trading operations for all buckets
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={processing ? <CircularProgress size={20} /> : <PlayIcon />}
              onClick={() => handleProcessOpportunities()}
              disabled={processing || monitoring}
              color="primary"
            >
              {processing ? 'Processing...' : 'Process Opportunities'}
            </Button>
            <Button
              variant="contained"
              startIcon={monitoring ? <CircularProgress size={20} /> : <TimerIcon />}
              onClick={() => handleMonitorPositions()}
              disabled={processing || monitoring}
              color="secondary"
            >
              {monitoring ? 'Monitoring...' : 'Monitor Positions'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Last Process Result */}
      {lastProcessResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Last Opportunity Processing
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Evaluated
                  </Typography>
                  <Typography variant="h6">
                    {lastProcessResult.results.opportunities_evaluated}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Opened
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {lastProcessResult.results.positions_opened}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Skipped
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {lastProcessResult.results.positions_skipped}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Errors
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {lastProcessResult.results.errors.length}
                  </Typography>
                </Box>
              </Grid>
              {lastProcessResult.results.errors.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="caption" fontWeight={600}>Errors:</Typography>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {lastProcessResult.results.errors.map((err: string, idx: number) => (
                        <li key={idx}><Typography variant="caption">{err}</Typography></li>
                      ))}
                    </ul>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Last Monitor Result */}
      {lastMonitorResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Last Position Monitoring
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Monitored
                  </Typography>
                  <Typography variant="h6">
                    {lastMonitorResult.results.positions_monitored}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Updated
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {lastMonitorResult.results.positions_updated}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Closed (Stop/Profit)
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {lastMonitorResult.results.positions_closed}
                  </Typography>
                </Box>
              </Grid>
              {lastMonitorResult.results.errors.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="caption" fontWeight={600}>Errors:</Typography>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {lastMonitorResult.results.errors.map((err: string, idx: number) => (
                        <li key={idx}><Typography variant="caption">{err}</Typography></li>
                      ))}
                    </ul>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Buckets Table */}
      {status && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Bucket Auto-Trading Status
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bucket Name</TableCell>
                    <TableCell align="center">Auto-Trading</TableCell>
                    <TableCell align="right">Available Capital</TableCell>
                    <TableCell align="right">Opportunities</TableCell>
                    <TableCell align="right">Open Positions</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status.buckets.map((bucket) => (
                    <TableRow key={bucket.bucket_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {bucket.bucket_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={bucket.auto_trading_enabled}
                          onChange={() =>
                            handleToggleAutoTrading(bucket.bucket_id, bucket.auto_trading_enabled)
                          }
                          color="success"
                        />
                        {bucket.auto_trading_enabled ? (
                          <Chip
                            label="ENABLED"
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        ) : (
                          <Chip
                            label="DISABLED"
                            size="small"
                            color="default"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(bucket.available_capital)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={bucket.available_opportunities}
                          size="small"
                          color={bucket.available_opportunities > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={bucket.open_positions}
                          size="small"
                          color={bucket.open_positions > 0 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="Process opportunities for this bucket">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleProcessOpportunities(bucket.bucket_id)}
                              disabled={processing || !bucket.auto_trading_enabled}
                            >
                              <PlayIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Monitor positions for this bucket">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleMonitorPositions(bucket.bucket_id)}
                              disabled={monitoring || bucket.open_positions === 0}
                            >
                              <TimerIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AutoTrading;
